// Acciones de estado del encabezado del editor: el paso principal (Publicar) como botón a la vista y
// lo demás en el menú «⋮». Con cambios sin guardar se apagan, salvo las que no tocan lo guardado.
import type { ReactNode } from 'react';
import { Archive, CalendarX, Copy, FlaskConical, Pause, Play, Send, Trash2 } from 'lucide-react';
import { ActionMenu, Button, usePermissions } from '@/kit';

export interface LifecycleAction {
    key: string;
    label: string;
    onSelect: () => void;
    /** Va como botón a la vista en vez de en el menú. */
    primary?: boolean;
    destructive?: boolean;
    icon?: ReactNode;
    /** Sin este permiso la acción no se muestra. */
    permission?: string;
    disabled?: boolean;
    /** Acciones que no tocan la versión guardada (p. ej. Duplicar): siguen disponibles con cambios sin guardar. */
    availableWhileDirty?: boolean;
    isLoading?: boolean;
}

/** Íconos de las acciones de siempre, para que la misma acción se vea igual en todas las pantallas. */
export const LIFECYCLE_ACTION_ICON = {
    publish: <Send className="size-3.5" aria-hidden />,
    pause: <Pause className="size-3.5" aria-hidden />,
    resume: <Play className="size-3.5" aria-hidden />,
    expire: <CalendarX className="size-3.5" aria-hidden />,
    archive: <Archive className="size-3.5" aria-hidden />,
    delete: <Trash2 className="size-3.5" aria-hidden />,
    duplicate: <Copy className="size-3.5" aria-hidden />,
    simulate: <FlaskConical className="size-3.5" aria-hidden />,
} as const;

export const DIRTY_ACTIONS_HINT = 'Guarda o descarta los cambios antes de cambiar el estado.';

export interface LifecycleActionsProps {
    actions: readonly LifecycleAction[];
    /** Hay cambios sin guardar: las acciones irían sobre la versión guardada. */
    dirty: boolean;
    menuLabel?: string;
}

export function LifecycleActions({
    actions,
    dirty,
    menuLabel = 'Acciones',
}: LifecycleActionsProps) {
    const { hasPermission } = usePermissions();
    const isOff = (action: LifecycleAction) =>
        action.disabled === true || (dirty && action.availableWhileDirty !== true);
    const primary = actions.filter(
        (action) =>
            action.primary === true &&
            (action.permission === undefined || hasPermission(action.permission)),
    );
    const menu = actions.filter((action) => action.primary !== true);

    return (
        <>
            {primary.map((action) => (
                <Button
                    key={action.key}
                    type="button"
                    size="sm"
                    variant={action.destructive ? 'destructive' : 'default'}
                    className="h-[26px] gap-1 px-2.5 text-[11px]"
                    disabled={isOff(action)}
                    isLoading={action.isLoading}
                    title={
                        dirty && action.availableWhileDirty !== true
                            ? DIRTY_ACTIONS_HINT
                            : undefined
                    }
                    onClick={action.onSelect}
                >
                    {action.icon}
                    {action.label}
                </Button>
            ))}
            {menu.length > 0 ? (
                <ActionMenu
                    label={menuLabel}
                    items={menu.map((action) => ({
                        label: action.label,
                        icon: action.icon,
                        destructive: action.destructive,
                        permission: action.permission,
                        disabled: isOff(action),
                        onSelect: action.onSelect,
                    }))}
                />
            ) : null}
        </>
    );
}
